import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { getApiUrl } from '../helpers/helperApp';
import swal from 'sweetalert';

export const useServerPagination = ({
    apiEndpoint,
    apiKey,
    perPage = 25,
    debounceTime = 500,
    setLoadingTableExternal = null,
    currentPage: externalCurrentPage,
    setCurrentPage: setCurrentPageExternal = null,
    idUpdate = null,
    initialPage = 1,
    additionalParams = {},
    requiredParams = [],
    sortColumn = '',
    sortDirection = 'asc'
}) => {
    const [data, setData] = useState([]);
    const [internalCurrentPage, setInternalCurrentPage] = useState(initialPage);
    const currentPage = externalCurrentPage !== undefined ? externalCurrentPage : internalCurrentPage;
    const setPage = setCurrentPageExternal
        ? (page) => { setCurrentPageExternal(page); setInternalCurrentPage(page); }
        : setInternalCurrentPage;

    const [perPageState, setPerPageState] = useState(perPage);
    const [totalPages, setTotalPages] = useState(0);
    const [totalRecords, setTotalRecords] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
    const [loadingTable, setLoadingTable] = useState(false);
    const [paginationInfo, setPaginationInfo] = useState('');

    const searchTimeout = useRef(null);

    // ─── Refs estables para additionalParams y requiredParams ────────────────
    // Sin esto, un objeto literal `{ supplierId: agentId }` crea una nueva
    // referencia en cada render → hasRequiredParams cambia → fetchData cambia
    // → el useEffect lo dispara → fetch → re-render → loop infinito.
    const additionalParamsRef = useRef(additionalParams);
    const requiredParamsRef = useRef(requiredParams);

    // Actualizamos las refs en cada render para que fetchData lea siempre
    // el valor más reciente sin necesitarlos como dependencia.
    useEffect(() => { additionalParamsRef.current = additionalParams; });
    useEffect(() => { requiredParamsRef.current = requiredParams; });

    // Trigger estable: solo sube cuando cambia el contenido real de additionalParams.
    // Permite que el useEffect de fetchData reaccione al cambio de agentId
    // sin depender del objeto directamente.
    const prevAdditionalParamsStr = useRef(null);
    const [fetchTrigger, setFetchTrigger] = useState(0);
    useEffect(() => {
        const next = JSON.stringify(additionalParams);
        if (prevAdditionalParamsStr.current !== next) {
            prevAdditionalParamsStr.current = next;
            setFetchTrigger(prev => prev + 1);
        }
    }, [additionalParams]);
    // ─────────────────────────────────────────────────────────────────────────

    const setLoading = useCallback((value) => {
        setLoadingTable(value);
        if (setLoadingTableExternal) {
            setLoadingTableExternal(value);
        }
    }, [setLoadingTableExternal]);

    // Sin dependencias → función estable. Lee siempre de la ref.
    const hasRequiredParams = useCallback(() => {
        return requiredParamsRef.current.every(key => {
            const val = additionalParamsRef.current[key];
            return val !== undefined && val !== null && val !== '';
        });
    }, []);

    // fetchData NO depende de additionalParams directamente → es estable.
    // Lee additionalParams desde la ref para tener siempre el valor actual.
    const fetchData = useCallback(async (page, search, showLoading = true) => {
        if (!hasRequiredParams()) {
            setData([]);
            setTotalPages(0);
            setTotalRecords(0);
            setPaginationInfo('');
            return;
        }

        if (showLoading) setLoading(true);
        try {
            const formData = new FormData();
            formData.append('validacion', apiKey());
            formData.append('page', page);
            formData.append('perPage', perPageState);
            if (search) formData.append('searchTerm', search);
            if (sortColumn) formData.append('sortColumn', sortColumn);
            if (sortDirection) formData.append('sortDirection', sortDirection);

            Object.entries(additionalParamsRef.current).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    formData.append(key, value);
                }
            });

            const url = `${getApiUrl(window.location.origin)}${apiEndpoint}`;
            const response = await axios.post(url, formData);
            if (response.data.success && Array.isArray(response.data.data)) {
                setData(response.data.data);
                const backendPagination = response.data.pagination || {};
                setTotalPages(backendPagination.totalPages || 0);
                setTotalRecords(backendPagination.totalRecords || 0);
            } else {
                swal('Error', 'Error en la respuesta del servidor', 'error');
                setData([]);
            }
        } catch (error) {
            swal('Error', 'Error al cargar datos paginados', 'error');
            setData([]);
        } finally {
            if (showLoading) setLoading(false);
        }
    }, [apiEndpoint, apiKey, perPageState, setLoading, hasRequiredParams, sortColumn, sortDirection]);
    // ↑ additionalParams ya NO está aquí → fetchData es estable entre renders

    const updatePaginationInfo = useCallback(() => {
        if (totalRecords > 0) {
            const perPageNum = parseInt(perPageState);
            const startRecord = (currentPage - 1) * perPageNum + 1;
            const endRecord = Math.min(currentPage * perPageNum, totalRecords);
            setPaginationInfo(
                `Mostrando registros del ${startRecord} al ${endRecord} de un total de ${totalRecords} registros`
            );
        } else {
            setPaginationInfo('');
        }
    }, [totalRecords, currentPage, perPageState]);

    // fetchTrigger reacciona al cambio real de additionalParams (ej: agentId llega tarde)
    useEffect(() => {
        fetchData(currentPage, debouncedSearchTerm);
    }, [currentPage, debouncedSearchTerm, fetchData, idUpdate, fetchTrigger, sortColumn, sortDirection]);

    useEffect(() => {
        updatePaginationInfo();
    }, [updatePaginationInfo]);

    const handleSearchChange = useCallback((value) => {
        setSearchTerm(value);
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(value);
        }, debounceTime);
    }, [debounceTime, setPage]);

    const handleDataTableSearch = useCallback((searchValue) => {
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setPage(1);
            setDebouncedSearchTerm(searchValue || '');
        }, debounceTime);
    }, [debounceTime, setPage]);

    const resetSearch = useCallback(() => {
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setPage(1);
    }, [setPage]);

    const handlePageChange = useCallback((newPage) => {
        if (newPage >= 1 && newPage <= totalPages) setPage(newPage);
    }, [totalPages, setPage]);

    const handleNextPage = useCallback(() => {
        if (currentPage < totalPages) setPage(currentPage + 1);
    }, [currentPage, totalPages, setPage]);

    const handlePreviousPage = useCallback(() => {
        if (currentPage > 1) setPage(currentPage - 1);
    }, [currentPage, setPage]);

    const getPaginationItems = useCallback(() => {
        const delta = 1;
        const left = currentPage - delta;
        const right = currentPage + delta + 1;
        const range = [];
        const rangeWithDots = [];
        let l;

        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= left && i < right)) range.push(i);
        }

        for (const i of range) {
            if (l) {
                if (i - l === 2) rangeWithDots.push(l + 1);
                else if (i - l !== 1) rangeWithDots.push('...');
            }
            rangeWithDots.push(i);
            l = i;
        }

        return rangeWithDots;
    }, [currentPage, totalPages]);

    const reload = useCallback(() => {
        fetchData(currentPage, debouncedSearchTerm, false);
    }, [currentPage, debouncedSearchTerm, fetchData]);

    const handlePageLengthChange = useCallback((newLength) => {
        setPerPageState(newLength);
        setPage(1);
    }, [setPage]);

    const handleSortChange = useCallback((column, direction) => {
        setPage(1);
    }, [setPage]);

    return {
        data,
        loadingTable,
        currentPage,
        totalPages,
        totalRecords,
        paginationInfo,
        searchTerm,
        handlePageChange,
        handleNextPage,
        handlePreviousPage,
        handleSearchChange,
        handleDataTableSearch,
        handlePageLengthChange,
        handleSortChange,
        resetSearch,
        getPaginationItems,
        reload,
        sortColumn,
        sortDirection
    };
};

export default useServerPagination;